import React, { useState, useEffect, useRef, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { useI18n } from "./i18n";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTheme } from "./ThemeContext";
import { fetchWallet } from "./client";
import { dispatchWalletChange, dispatchMarketChange,
   onWalletChange, onMarketChange } from "./walletEvents";
import NavIcon from "./NavIcons";
import NotificationBell from "./NotificationBell";
import AnimatedLogo from "./AnimatedLogo";

/* ─── Animations ─────────────────────────────────────────────────────────── */
const fadeDown = keyframes`from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}`;
const pulse    = keyframes`0%,100%{opacity:1}50%{opacity:.4}`;

const slideUp   = keyframes`from{transform:translateY(0)}to{transform:translateY(-100%)}`;
const slideDown = keyframes`from{transform:translateY(-100%)}to{transform:translateY(0)}`;

/* ─── Main bar ───────────────────────────────────────────────────────────── */
const Bar = styled.nav`
  position: sticky; top: 0; z-index: 200;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 1.5rem; height: 56px; gap: .75rem;
  background: ${({theme}) => theme.fonNav}; 
  backdrop-filter: blur(16px);
  border-bottom: 1px solid ${({theme}) => theme.border};
  /* Hide/show sur scroll */
  transform: translateY(${({$hidden}) => $hidden ? "-100%" : "0"});
  transition: transform .28s cubic-bezier(.4,0,.2,1),
              box-shadow .2s ease;
  box-shadow: ${({$scrolled}) => $scrolled
    ? "0 2px 16px rgba(0,0,0,.18)"
    : "none"};

  @media (max-width: 480px) {
    padding: 0 .65rem;
    gap: .4rem;
  }
`;

const Logo = styled.div`
  font-family: ${({theme}) => theme.fontDisplay};
  font-size: 1.15rem; font-weight: 700; letter-spacing: -.02em;
  cursor: pointer; flex-shrink: 0;
  span { color: ${({theme}) => theme.green}; }

  @media (max-width: 340px) {
    font-size: 1rem;
    span.suffix { display: none; }
  }
`;

/* ─── Desktop nav ────────────────────────────────────────────────────────── */
const DesktopNav = styled.div`
  display: flex; align-items: center; gap: .1rem; flex: 1;
  justify-content: center; overflow-x: auto; scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
  @media (max-width: 768px) { display: none; }
`;

const NavBtn = styled.button`
  display: flex; align-items: center; gap: .4rem;
  background: ${({$active,theme}) => $active ? theme.bgHover : "transparent"};
  border: 1px solid ${({$active,theme}) => $active ? theme.borderLight : "transparent"};
  color: ${({$active,theme}) => $active ? theme.text : theme.text};
  font-family: ${({theme}) => theme.fontBody}; font-size: .78rem; font-weight: 500;
  padding: .32rem .8rem; border-radius: 6px; cursor: pointer;
  transition: all .15s; white-space: nowrap;
  &:hover { color: ${({theme}) => theme.text}; background: ${({theme}) => theme.bgHover}; }
`;

const AdminBtn = styled(NavBtn)`
  color: ${({$active,theme}) => $active ? theme.gold : theme.textMuted};
  border-color: ${({$active,theme}) => $active ? theme.gold+"55" : "transparent"};
  background: ${({$active}) => $active ? "rgba(255,193,7,.08)" : "transparent"};
  &:hover { color: ${({theme}) => theme.gold}; }
`;

/* ─── Right section ──────────────────────────────────────────────────────── */
const Right = styled.div`
  display: flex; align-items: center; gap: .45rem; flex-shrink: 0;
`;

const IconBtn = styled.button`
  width: 32px; height: 32px; border-radius: 7px; flex-shrink: 0;
  border: 1px solid ${({theme}) => theme.border};
  background: transparent; color: ${({theme}) => theme.text};
  cursor: pointer; transition: all .15s;
  display: flex; align-items: center; justify-content: center;
  &:hover { border-color: ${({theme}) => theme.borderLight}; color: ${({theme}) => theme.text}; }
    @media (max-width: 780px) {
      display: ${({user}) => user?"none":"block"};
  }
`;

const Balance = styled.div`
  font-family: ${({theme}) => theme.fontMono}; font-size: .7rem;
  color: ${({theme}) => theme.textDim}; white-space: nowrap;
  span { color: ${({theme}) => theme.green}; font-weight: 500; }
  cursor: ${({$click}) => $click ? "pointer" : "default"};
  transition: opacity .15s;
  &:hover { opacity: ${({$click}) => $click ? ".75" : "1"}; }
  &:focus-visible { outline: 1px solid ${({theme}) => theme.green}; outline-offset: 3px; border-radius: 4px; }
`;

const BalanceMobile = styled.div`
  font-family: ${({theme}) => theme.fontMono}; font-size: .7rem;
  color: ${({theme}) => theme.textDim}; white-space: nowrap;
  display: flex; align-items: center;
  span { color: ${({theme}) => theme.green}; font-weight: 500; }
  cursor: ${({$click}) => $click ? "pointer" : "default"};
  &:active { opacity: ${({$click}) => $click ? ".7" : "1"}; }
  @media (min-width: 768px) { display: none; }
`;

const LiveDot = styled.span`
  width: 6px; height: 6px; border-radius: 50%;
  background: ${({theme}) => theme.green};
  display: inline-block; margin-right: .35rem;
  animation: ${pulse} 2s infinite;
`;

const AuthBtn = styled.button`
height: 32px;
  background: ${({$primary,theme}) => $primary ? theme.green : "transparent"};
  border: 1px solid ${({$primary,theme}) => $primary ? theme.green : theme.borderLight};
  color: ${({$primary}) => $primary ? "#000" : "inherit"};
  font-family: ${({theme}) => theme.fontBody}; font-size: .76rem; font-weight: 600;
  padding: .32rem .85rem; border-radius: 6px; cursor: pointer;
  transition: all .15s; white-space: nowrap;
  &:hover { opacity: .85; }

  @media (max-width: 400px) {
  
  display:  ${({$primary,theme}) => $primary ? "none" : "block"};
  background: ${({$primary,theme}) => $primary ? "transparent" :  theme.green };
    color: ${({$primary}) => $primary ? "#000" : "#000"};

   padding: .28rem .55rem; font-size: .68rem; }
  @media (max-width: 340px) { padding: .25rem .45rem; font-size: .64rem; }
`;

const DesktopOnly = styled.div`
  display: flex; align-items: center; gap: .45rem;
  @media (max-width: 768px) { display: none; }
`;

const MobileOnly = styled.div`
  display: none;
  @media (max-width: 768px) { display: flex; align-items: center; }
`;

const Username = styled.span`
  font-size: .74rem; color: ${({theme}) => theme.textMuted};
  @media (max-width: 600px) { display: none; }
`;

/* ─── Burger ─────────────────────────────────────────────────────────────── */
const Burger = styled.button`
  display: none;
  width: 34px; height: 34px; border-radius: 7px; flex-shrink: 0;
  border: 1px solid ${({theme}) => theme.border};
  background: ${({$open,theme}) => $open ? theme.bgHover : "transparent"};
  color: ${({theme}) => theme.text};
  cursor: pointer; transition: all .15s;
  align-items: center; justify-content: center;
  @media (max-width: 768px) { display: flex; }
`;

/* ─── Mobile drawer ──────────────────────────────────────────────────────── */
const Drawer = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: ${({$open}) => $open ? "flex" : "none"};
    flex-direction: column;
    position: fixed;
    top: ${({$hidden}) => $hidden ? "0" : "56px"};
    left: 0; right: 0;
    background: ${({theme}) => theme.bgCard};
    border-bottom: 1px solid ${({theme}) => theme.border};
    padding: .6rem;
    z-index: 199;
    animation: ${fadeDown} .2s ease;
    max-height: calc(100vh - 56px);
    overflow-y: auto;
    transition: top .28s cubic-bezier(.4,0,.2,1);
  }
`;

const DrawerItem = styled.button`
  width: 100%; display: flex; align-items: center; gap: .7rem;
  padding: .75rem 1rem; border-radius: 8px; border: none;
  background: ${({$active,theme}) => $active ? theme.bgHover : "transparent"};
  color: ${({$active,theme}) => $active ? theme.text : theme.textDim};
  font-family: ${({theme}) => theme.fontBody}; font-size: .85rem;
  font-weight: ${({$active}) => $active ? 600 : 400};
  cursor: pointer; text-align: left; transition: all .15s;
  border-left: 2px solid ${({$active,theme}) => $active ? theme.accent : "transparent"};
  &:hover { background: ${({theme}) => theme.bgHover}; color: ${({theme}) => theme.text}; }

    @media (max-width: 768px) { .none { display: none;} }

`;

const DrawerAdminItem = styled(DrawerItem)`
  color: ${({$active,theme}) => $active ? theme.gold : theme.textMuted};
  border-left-color: ${({$active,theme}) => $active ? theme.gold : "transparent"};
  &:hover { color: ${({theme}) => theme.gold}; }
`;

const DrawerSep = styled.div`
  height: 1px; background: ${({theme}) => theme.border};
  margin: .4rem .5rem;
`;

const DrawerBottom = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: .75rem 1rem; gap: .6rem; flex-wrap: wrap;
`;

const DrawerBalanceRow = styled.div`
  font-family: ${({theme}) => theme.fontMono}; font-size: .72rem;
  color: ${({theme}) => theme.textDim};
  display: flex; align-items: center;
  span { color: ${({theme}) => theme.green}; font-weight: 500; }
  cursor: ${({$click}) => $click ? "pointer" : "default"};
  &:active { opacity: ${({$click}) => $click ? ".7" : "1"}; }
`;

/* Hide on mobile */
const HideOnMobile = styled.div`
  display: flex; align-items: center; gap: .45rem;
  @media (max-width: 768px) { display: none; }
`;

/* Show only on mobile */
const MobileAuthRow = styled.div`
  display: none;
  @media (max-width: 768px) { display: flex; align-items: center; gap: .4rem; }
`;

/* ─── Overlay ────────────────────────────────────────────────────────────── */
const Overlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: ${({$open}) => $open ? "block" : "none"};
    position: fixed; inset: 0; top: 56px;
    z-index: 198;
    background: rgba(0,0,0,.5);
  }
`;

/* ─── Traductions menu secondaire (Légal/Société/Ressources/FAQ) ─────────── */
const SECONDARY_LABELS = {
  legal:     { fr:"Mentions légales", en:"Legal",      es:"Aviso legal",    de:"Impressum",   pt:"Aviso legal",     ar:"الإشعارات القانونية" },
  company:   { fr:"Entreprise",       en:"Company",    es:"Empresa",        de:"Unternehmen", pt:"Empresa",         ar:"الشركة" },
  resources: { fr:"Ressources",       en:"Resources",   es:"Recursos",       de:"Ressourcen",  pt:"Recursos",        ar:"الموارد" },
  faq:       { fr:"FAQ",              en:"FAQ",         es:"FAQ",            de:"FAQ",         pt:"FAQ",             ar:"الأسئلة الشائعة" },
  themeLight:{ fr:"Mode clair",       en:"Light mode",  es:"Modo claro",     de:"Heller Modus",pt:"Modo claro",      ar:"الوضع الفاتح" },
  themeDark: { fr:"Mode sombre",      en:"Dark mode",   es:"Modo oscuro",    de:"Dunkler Modus",pt:"Modo escuro",    ar:"الوضع الداكن" },
};
function sl(key, lang) { return SECONDARY_LABELS[key]?.[lang] ?? SECONDARY_LABELS[key]?.fr ?? key; }

/* ─── Component ──────────────────────────────────────────────────────────── */
export default function Navbar({ view, setView, navigate: navigateApp, user, onLoginClick, onLogout, drawerOpen, setDrawerOpen }) {
  const { t, lang } = useI18n();
  const { isDark, toggleTheme } = useTheme();
  const [balance, setBalance] = useState(null);
  const drawerRef = useRef(null);

  /* ── Hide-on-scroll ──────────────────────────────────────────────────── */
  const [hidden,   setHidden]   = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const lastY = useRef(0);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(y > 10);
        /* Ne pas cacher si le drawer mobile est ouvert */
        if (!drawerOpen) {
          if (y > lastY.current + 8 && y > 80) {
            setHidden(true);   // scroll vers le bas → cache
          } else if (y < lastY.current - 4) {
            setHidden(false);  // scroll vers le haut → montre
          }
        }
        lastY.current = y;
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [drawerOpen]);

  /* Toujours visible quand le drawer est ouvert */
  useEffect(() => { if (drawerOpen) setHidden(false); }, [drawerOpen]);

  const loadBalance = useCallback(async () => {
    if (!user) { setBalance(null); return; }
    try {
      const res = await fetchWallet();
      setBalance(parseFloat(res.data.usdc_balance || 0).toFixed(2));
    } catch {
      setBalance("—");
    }
  }, [user]);

  useEffect(() => {
    loadBalance();
    if (!user) return;
    const interval = setInterval(loadBalance, 900000);
    return () => clearInterval(interval);
  }, [loadBalance, user]);

  useEffect(() => { if (view === "wallet") loadBalance(); }, [view, loadBalance]);
  useEffect(() => { setDrawerOpen(false); }, [view]);
  useEffect(() => {
    const handler = () => { if (window.innerWidth > 768) setDrawerOpen(false); };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  useEffect(() => onWalletChange(() => loadBalance()), [loadBalance]);

  const navigate = (v) => { setView(v); setDrawerOpen(false); };

  /* Le solde est cliquable (connecté uniquement) → ouvre le Wallet */
  const walletProps = user ? {
    $click: true, role: "button", tabIndex: 0, title: t("nav.wallet"),
    onClick: () => navigate("wallet"),
    onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("wallet"); } },
  } : {};

  /* Items principaux — icône réelle au lieu d'emoji */
  const NAV_ITEMS = [
    { id:"howto",     label:t("nav.howto"),     icon:"howto",     always:true },
    { id:"markets",   label:t("nav.markets"),   icon:"markets",   always:true },
    { id:"positions", label:t("nav.positions"), icon:"positions", auth:true },
    { id:"wallet",    label:t("nav.wallet"),    icon:"wallet",    auth:true },
    { id:"support",   label:t("nav.support"),   icon:"support",   auth:true },
  ];

  /* Items secondaires — toujours visibles, accès public (Légal, Société, Ressources, FAQ) */
  const SECONDARY_ITEMS = [
    { id:"legal",     icon:"legal" },
    { id:"company",   icon:"company" },
    { id:"resources",  icon:"resources" },
    { id:"faq",        icon:"faq" },
  ];

  return (
    <>
      <Bar $hidden={hidden} $scrolled={scrolled}>
        <Logo onClick={() => navigate("markets")}><AnimatedLogo isDark ={isDark} width={120} /></Logo>

        <DesktopNav>
          {NAV_ITEMS.filter(i => i.always || (i.auth && user)).map(item => (
            <NavBtn key={item.id} $active={view === item.id} onClick={() => navigate(item.id)}>
              <NavIcon name={item.icon} size={15} />
              {item.label}
            </NavBtn>
          ))}

          {(user?.isStaff) && (
            <NavBtn $active={(view === "employee" || view === "admin")} onClick={() => navigate("employee")}>
              <NavIcon name="employee" size={15} />
              {t("nav.employee")}
            </NavBtn>
          )}
          {user?.isAdmin && (
            <AdminBtn $active={view === "admin"} onClick={() => navigate("admin")}>
              <NavIcon name="admin" size={15} />
              {t("nav.admin")}
            </AdminBtn>
          )}
        </DesktopNav>

        <Right>
          <IconBtn user={user?true:false} onClick={toggleTheme} title={isDark ? "Passer en mode clair" : "Passer en mode sombre"}>
            <NavIcon name={isDark ? "sun" : "moon"} size={16} />
          </IconBtn>

          <LanguageSwitcher compact user={user?true:false} />

          <DesktopOnly>
            <Balance {...walletProps}>
              <LiveDot />
              {user
                ? <><span>{t("nav.balance")}:</span> <span>{balance !== null ? `${balance} EVP` : "…"}</span></>
                : <span style={{color:isDark ? "#8a9aaa" : "#333"}}>{t("nav.notconnected")}</span>}
            </Balance>
          </DesktopOnly>

          {user ? (
            <>
              <DesktopOnly>
                <NotificationBell user={user} navigate={navigateApp} />
                <IconBtn onClick={() => navigate("settings")} title={t("page.title")}>
                  <NavIcon name="settings"  size={16} />
                </IconBtn>
                <Username>{user.username}</Username>
                <AuthBtn onClick={onLogout}>{t("nav.signout")}</AuthBtn>
              </DesktopOnly>

              <BalanceMobile style={{fontSize:".68rem"}} {...walletProps}>
                <LiveDot />
                <span>{balance ?? "…"} EVP</span>
              </BalanceMobile>

              <MobileOnly>
                <NotificationBell user={user} navigate={navigateApp} />
              </MobileOnly>

              <Burger $open={drawerOpen} onClick={() => setDrawerOpen(o => !o)} aria-label="Menu">
                <NavIcon name={drawerOpen ? "close" : "menu"} size={17} />
              </Burger>
            </>
          ) : (
            <>
              <AuthBtn onClick={() => onLoginClick("login")}>{t("nav.signin")}</AuthBtn>
              <AuthBtn $primary onClick={() => onLoginClick("signup")}>{t("nav.signup")}</AuthBtn>
            </>
          )}
        </Right>
      </Bar>

      <Overlay $open={drawerOpen} onClick={() => setDrawerOpen(false)} />

      <Drawer $open={drawerOpen} $hidden={hidden} ref={drawerRef}>
        {NAV_ITEMS.filter(i => i.always || (i.auth && user)).map(item => (
          <DrawerItem key={item.id} $active={view === item.id} onClick={() => navigate(item.id)}>
            <NavIcon name={item.icon} size={18} />
            {item.label}
          </DrawerItem>
        ))}

        {user?.isStaff && (
          <DrawerItem $active={view === "employee"} onClick={() => navigate("employee")}>
            <NavIcon name="employee" size={18} />
            {t("nav.employee")}
          </DrawerItem>
        )}
        {user?.isAdmin && (
          <DrawerAdminItem $active={view === "admin"} onClick={() => navigate("admin")}>
            <NavIcon name="admin" size={18} />
            {t("nav.admin")}
          </DrawerAdminItem>
        )}

        <DrawerSep />

        {/* ── Légal / Société / Ressources / FAQ ── */}
        {SECONDARY_ITEMS.map(item => (
          <DrawerItem key={item.id} $active={view === item.id} onClick={() => navigate(item.id)}>
            <NavIcon name={item.icon} size={18} />
            {sl(item.id, lang)}
          </DrawerItem>
        ))}

        <DrawerItem className="none" onClick={toggleTheme}>
          <NavIcon name={isDark ? "sun" : "moon"} size={18} />
          {isDark
            ? (sl("themeLight", lang) ?? "Mode clair")
            : (sl("themeDark", lang) ?? "Mode sombre")}
        </DrawerItem>

        <DrawerSep />

        {user ? (
          <>
            <DrawerItem onClick={() => navigate("settings")}>
              <NavIcon name="settings" size={18} />
              {t("nav.account_settings")}
            </DrawerItem>
            <DrawerBottom>
              <DrawerBalanceRow {...walletProps}>
                <LiveDot /> {t("nav.balance")}: <span>{balance ?? "…"} EVP</span>
              </DrawerBalanceRow>
              <AuthBtn
                onClick={() => { onLogout(); setDrawerOpen(false); }}
                style={{display:"flex",alignItems:"center",gap:".35rem",border:"1px solid rgba(255,59,92,.4)", color:"#ff3b5c", background:"rgba(255,59,92,.08)"}}
              >
                <NavIcon name="logout" size={14} />
                {t("nav.signout")}
              </AuthBtn>
            </DrawerBottom>
          </>
        ) : (
          <DrawerBottom>
            <AuthBtn style={{flex:1}} onClick={() => { onLoginClick("login"); setDrawerOpen(false); }}>
              {t("nav.signin")}
            </AuthBtn>
            <AuthBtn $primary style={{flex:1}} onClick={() => { onLoginClick("signup"); setDrawerOpen(false); }}>
              {t("nav.signup")}
            </AuthBtn>
          </DrawerBottom>
        )}
      </Drawer>
    </>
  );
}
